import './RemoveItemsModal.css';
import React from 'react';
import { removeItems } from '../../../../model/item';
import { IncomingItemGroup, OutgoingItemGroup } from '../../../../model/order';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { FormForm } from '../../../../components/Form/FormForm';
import { FormRadioGroup } from '../../../../components/Form/Radio/FormRadioGroup';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { Label } from '../../../../components/Form/Label/Label';
import { Stack } from '../../../../components/Stack/Stack';

type RemoveItemsOption = 'release' | 'block';

interface Props {
  orderId: string;
  itemGroups: Array<OutgoingItemGroup | IncomingItemGroup>;
  itemIds: string[];
  onChange: () => void;
}

export function RemoveItemsModal({ orderId, itemGroups, itemIds, onChange }: Props): JSX.Element {
  const { open, Modal, size, close } = useModal();

  const events = itemGroups.filter((group) => group.product?.__typename === 'Event');
  const products = itemGroups.filter((group) => group.product?.__typename !== 'Event');

  /* Only the tickets are blocked, so any merch in the selection is released either way. */
  const ticketItemIds = new Set(events.flatMap((group) => group.itemIds));

  const { model, fields, onSubmit, valid, submissionStatus } = useForm<{
    option: RemoveItemsOption;
    blockReason: string;
  }>({
    model: { option: 'release', blockReason: '' },
    validations: {
      blockReason: ({ model }) => (model.option === 'block' && !model.blockReason ? ['required'] : []),
    },
    onSubmit: async ({ model }) => {
      const blocking = model.option === 'block';
      await removeItems({
        orderId,
        itemIds: itemIds.filter((id) => !blocking || !ticketItemIds.has(id)),
        blockedItemIds: blocking ? itemIds.filter((id) => ticketItemIds.has(id)) : [],
        blockReason: blocking ? model.blockReason : undefined,
      });
      onChange();
      close();
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={t.actions.remove(t.model.item._plural)} size={size}>
      <FormForm
        className="remove-items-modal"
        onSubmit={onSubmit}
        onClose={close}
        canSubmit={valid}
        loading={submissionStatus === 'submitting'}
        saveButtonTitle={t.actions.remove('')}
      >
        <Stack>
          {!!products.length && (
            <p className="remove-items-modal__products">{t.model.item.removeModal.mixedWarning(events.length)}</p>
          )}
          <Label title={t.model.item.removeModal.ticketOptions(events.length)} />
          <FormRadioGroup<RemoveItemsOption>
            {...fields.option}
            name="option"
            options={[
              { label: t.model.item.removeModal.removeAndRelease, value: 'release' },
              { label: t.model.item.removeModal.removeAndBlock, value: 'block' },
            ]}
          />
          {model.option === 'block' && (
            <FormTextField {...fields.blockReason} label={t.model.contingent.blockReason} required />
          )}
        </Stack>
      </FormForm>
    </Modal>
  );
}
